import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { Placeholder } from '@tiptap/extensions';
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Redo2,
  Table as TableIcon,
  TextQuote,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/core';
import {
  EMPTY_GUIDE_DOC,
  GUIDE_TYPOGRAPHY_CLASS,
  guideExtensions,
} from './guideContent';

const ToolbarButton = ({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type='button'
    aria-label={label}
    title={label}
    disabled={disabled}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    className={`rounded p-1.5 transition disabled:opacity-40 ${
      active
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className='mx-1 h-5 w-px bg-gray-200' />;

const GuideToolbar = ({ editor }: { editor: Editor }) => {
  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('ลิงก์ (URL)', previous || 'https://');
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url.trim() })
      .run();
  };
  return (
    <div className='flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5'>
      <ToolbarButton
        label='หัวข้อใหญ่'
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='หัวข้อย่อย'
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 size={16} />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        label='ตัวหนา'
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='ตัวเอียง'
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='ขีดเส้นใต้'
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={16} />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        label='รายการ'
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='รายการตัวเลข'
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='เช็คลิสต์'
        active={editor.isActive('taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <ListChecks size={16} />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        label='ข้อความอ้างอิง'
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <TextQuote size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='โค้ด'
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='ลิงก์'
        active={editor.isActive('link')}
        onClick={setLink}
      >
        <Link2 size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='ตาราง'
        active={editor.isActive('table')}
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      >
        <TableIcon size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='เส้นคั่น'
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus size={16} />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        label='ย้อนกลับ'
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='ทำซ้ำ'
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 size={16} />
      </ToolbarButton>
    </div>
  );
};

interface GuideEditorProps {
  initialContent: unknown;
  // Called with the full document on every change; the parent keeps the
  // latest value (typically in a ref) and reads it when saving.
  onDocChange: (doc: JSONContent) => void;
}

const GuideEditor = ({ initialContent, onDocChange }: GuideEditorProps) => {
  const editor = useEditor({
    extensions: [
      ...guideExtensions,
      Placeholder.configure({ placeholder: 'พิมพ์เนื้อหาที่นี่…' }),
    ],
    content:
      initialContent && typeof initialContent === 'object'
        ? (initialContent as JSONContent)
        : EMPTY_GUIDE_DOC,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: instance }) => onDocChange(instance.getJSON()),
    editorProps: {
      attributes: {
        class: `${GUIDE_TYPOGRAPHY_CLASS} min-h-[16rem] px-4 py-3 focus:outline-none`,
      },
    },
  });

  if (!editor) return null;

  return (
    <div className='overflow-hidden rounded-md border border-gray-300 bg-white focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'>
      <GuideToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default GuideEditor;
